import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useLanguage } from './LanguageContext';

// Import mock data as fallback
import {
  heroContent as mockHero,
  services as mockServices,
  benefits as mockBenefits,
  sponsor as mockSponsor,
  partners as mockPartners,
  aboutContent as mockAbout,
  contactInfo as mockContact,
  footerContent as mockFooter
} from '../data/mock';

const ContentContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const ContentProvider = ({ children }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // CMS data state
  const [cmsData, setCmsData] = useState({
    hero: null,
    services: null,
    about: null,
    values: null,
    partners: null,
    news: null,
    jobs: null,
    contactInfo: null,
    settings: null
  });

  // Fetch all CMS data on mount
  useEffect(() => {
    const fetchAllContent = async () => {
      setLoading(true);
      try {
        const endpoints = [
          '/api/cms/hero',
          '/api/cms/services',
          '/api/cms/about',
          '/api/cms/values',
          '/api/cms/partners',
          '/api/cms/news?published_only=true',
          '/api/cms/jobs?published_only=true',
          '/api/cms/contact-info',
          '/api/cms/settings'
        ];

        const results = await Promise.all(
          endpoints.map(endpoint => 
            fetch(`${API_URL}${endpoint}`)
              .then(r => r.ok ? r.json() : null)
              .catch(() => null)
          )
        );

        const [heroRes, servicesRes, aboutRes, valuesRes, partnersRes, newsRes, jobsRes, contactInfoRes, settingsRes] = results;

        setCmsData({
          hero: heroRes,
          services: servicesRes,
          about: aboutRes,
          values: valuesRes,
          partners: partnersRes,
          news: newsRes,
          jobs: jobsRes,
          contactInfo: contactInfoRes,
          settings: settingsRes
        });
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching CMS content:', err);
        }
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllContent();
  }, []);

  // Transform CMS hero data to match mock format, with language support
  const getHeroContent = useCallback(() => {
    if (cmsData.hero && cmsData.hero.headline_de) {
      const h = cmsData.hero;
      return {
        tagline: language === 'de' ? 'Exzellenz in Hospitality' : 'Excellence in Hospitality',
        headline: language === 'de' ? h.headline_de : h.headline_en,
        subheadline: language === 'de' ? h.subheadline_de : h.subheadline_en,
        description: language === 'de' ? h.description_de : h.description_en,
        ctaText: language === 'de' ? h.cta_de : h.cta_en,
        videoUrl: h.video_url || mockHero.videoUrl,
        // Bottom section fields
        bottomTitle: language === 'de' ? (h.bottom_title_de || 'Unsere Dienstleistungen') : (h.bottom_title_en || 'Our Services'),
        bottomSubtitle: language === 'de' ? (h.bottom_subtitle_de || 'Wir bieten') : (h.bottom_subtitle_en || 'We offer')
      };
    }
    return mockHero;
  }, [cmsData.hero, language]);

  // Transform CMS services to match mock format
  const getServices = useCallback(() => {
    if (cmsData.services && cmsData.services.length > 0) {
      return cmsData.services.map((s, index) => ({
        id: s.id || index + 1,
        title: language === 'de' ? s.title_de : s.title_en,
        subtitle: '', // CMS doesn't have subtitle
        description: language === 'de' ? s.description_de : s.description_en,
        image: s.image_url || '',
        videoUrl: s.video_url || '',
        icon: s.icon || 'sparkles'
      }));
    }
    return mockServices;
  }, [cmsData.services, language]);

  // Transform CMS about data
  const getAboutContent = useCallback(() => {
    if (cmsData.about && cmsData.about.name) {
      const a = cmsData.about;
      return {
        name: a.name,
        role: language === 'de' ? a.role_de : a.role_en,
        image: a.image_url || mockAbout.image,
        bio: language === 'de' ? (a.bio_de || mockAbout.bio) : (a.bio_en || mockAbout.bio)
      };
    }
    return mockAbout;
  }, [cmsData.about, language]);

  // Transform CMS values/benefits
  const getBenefits = useCallback(() => {
    if (cmsData.values && cmsData.values.length > 0) {
      return cmsData.values.map((v, index) => ({
        id: v.id || index + 1,
        title: language === 'de' ? v.title_de : v.title_en,
        description: language === 'de' ? v.description_de : v.description_en,
        icon: v.icon || 'star'
      }));
    }
    return mockBenefits;
  }, [cmsData.values, language]);

  // Transform CMS partners
  const getPartners = useCallback(() => {
    if (cmsData.partners && cmsData.partners.length > 0) {
      return cmsData.partners
        .filter(p => !p.is_sponsor)
        .map((p, index) => ({
          id: p.id || index + 1,
          name: p.name,
          logo: p.logo_url
        }));
    }
    return mockPartners;
  }, [cmsData.partners]);

  // Get sponsor from partners
  const getSponsor = useCallback(() => {
    if (cmsData.partners && cmsData.partners.length > 0) {
      const sponsorData = cmsData.partners.find(p => p.is_sponsor);
      if (sponsorData) {
        return {
          name: sponsorData.name,
          description: language === 'de' ? sponsorData.description_de : sponsorData.description_en,
          logo: sponsorData.logo_url
        };
      }
    }
    return mockSponsor;
  }, [cmsData.partners, language]);

  // Get news articles
  const getNews = useCallback(() => {
    if (cmsData.news && cmsData.news.length > 0) {
      return cmsData.news.map(n => ({
        id: n.id,
        title: language === 'de' ? n.title_de : n.title_en,
        excerpt: language === 'de' ? n.excerpt_de : n.excerpt_en,
        content: language === 'de' ? n.content_de : n.content_en,
        category: language === 'de' ? n.category_de : n.category_en,
        image: n.image_url,
        date: n.date
      }));
    }
    return [];
  }, [cmsData.news, language]);

  // Get job listings
  const getJobs = useCallback(() => {
    if (cmsData.jobs && cmsData.jobs.length > 0) {
      return cmsData.jobs.map(j => ({
        id: j.id,
        title: language === 'de' ? j.title_de : j.title_en,
        type: language === 'de' ? j.type_de : j.type_en,
        location: j.location,
        description: language === 'de' ? j.description_de : j.description_en,
        requirements: language === 'de' ? j.requirements_de : j.requirements_en
      }));
    }
    return [];
  }, [cmsData.jobs, language]);

  // Get contact info
  const getContactInfo = useCallback(() => {
    if (cmsData.contactInfo) {
      return {
        ...cmsData.contactInfo,
        hours: language === 'de' ? cmsData.contactInfo.hours_de : cmsData.contactInfo.hours_en
      };
    }
    return mockContact;
  }, [cmsData.contactInfo, language]);

  // Get company settings
  const getSettings = useCallback(() => {
    if (cmsData.settings) {
      return {
        ...cmsData.settings,
        tagline: language === 'de' ? cmsData.settings.tagline_de : cmsData.settings.tagline_en
      };
    }
    return mockFooter;
  }, [cmsData.settings, language]);

  // Memoize state values separately to reduce dependency count
  const stateValues = useMemo(() => ({ loading, error }), [loading, error]);

  // Memoize hero content
  const heroContent = useMemo(() => getHeroContent(), [getHeroContent]);
  
  // Memoize services
  const servicesData = useMemo(() => getServices(), [getServices]);
  
  // Memoize benefits
  const benefitsData = useMemo(() => getBenefits(), [getBenefits]);
  
  // Memoize about content
  const aboutData = useMemo(() => getAboutContent(), [getAboutContent]);
  
  // Memoize partners and sponsor
  const partnersData = useMemo(() => getPartners(), [getPartners]);
  const sponsorData = useMemo(() => getSponsor(), [getSponsor]);
  
  // Memoize news and jobs
  const newsData = useMemo(() => getNews(), [getNews]);
  const jobsData = useMemo(() => getJobs(), [getJobs]);
  
  // Memoize contact and settings
  const contactData = useMemo(() => getContactInfo(), [getContactInfo]);
  const settingsData = useMemo(() => getSettings(), [getSettings]);

  // Final context value with pre-memoized data
  const value = useMemo(() => ({
    ...stateValues,
    heroContent,
    services: servicesData,
    benefits: benefitsData,
    aboutContent: aboutData,
    partners: partnersData,
    sponsor: sponsorData,
    news: newsData,
    jobs: jobsData,
    contactInfo: contactData,
    settings: settingsData,
    rawCmsData: cmsData
  }), [
    stateValues, heroContent, servicesData, benefitsData, aboutData,
    partnersData, sponsorData, newsData, jobsData, contactData, settingsData, cmsData
  ]);

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

export default ContentContext;
